/**
 * What `delivery` on a goods document says (#160, design D6).
 *
 * `delivery` is `null` until a driver confirms a consignment from the DAC app; after that it counts
 * how many of the document's **current** consignments have a confirmation. Two questions are asked
 * of it, and they have different answers on purpose:
 *
 *  - **How far is it delivered?** — the list's tag. «Entregado» when every consignment is
 *    confirmed, «Entregado k/n» while some are, and nothing at `0`: a confirmation whose number
 *    the document no longer has (an in-place amendment shortened its list) delivers none of them.
 *  - **Has it any confirmation at all?** — the cancel dialogs' warning, which is about what is kept
 *    with the document, and so counts that confirmation too.
 *
 * Both take `delivery` as optional, though the contract makes it required on `Deca` and
 * `DecaSummary` and those are all the portal passes: absent reads as `null`, never as a claim either
 * way. No version's stored payload reaches them. `DecaVersion.payload` is not a `Deca`: it is the
 * record as it stood before an in-place amendment, under the service's internal field names, or `{}`
 * for a retirement, it carries no `delivery`, and the portal does not read it.
 */

import type { DeliverySummary } from '@deca/api'

export type DeliveryProgress =
  | { kind: 'delivered' }
  | { kind: 'partly'; confirmed: number; total: number }

/**
 * The tag's answer, or `null` for no tag.
 *
 * Also `null` for a pair the server never sends — more confirmed than there are consignments, or no
 * count — rather than an «Entregado» the portal cannot check or an «Entregado 4/3».
 */
export function deliveryProgress(
  delivery: DeliverySummary | null | undefined,
  consignmentCount: number | null | undefined,
): DeliveryProgress | null {
  if (!delivery || consignmentCount == null) return null
  const confirmed = delivery.confirmedConsignments
  if (confirmed <= 0) return null
  if (confirmed === consignmentCount) return { kind: 'delivered' }
  if (confirmed < consignmentCount) return { kind: 'partly', confirmed, total: consignmentCount }
  return null
}

/** Whether a document has any delivery confirmation — the cancel dialogs' question. */
export function hasDeliveryConfirmation(subject: { delivery?: DeliverySummary | null }): boolean {
  return subject.delivery != null
}
